import React, { useMemo } from 'react';
import {
  Drawer,
  List,
  Badge,
  Typography,
  Avatar,
  Space,
  Button,
  Empty,
  Tag,
  Spin,
  Alert,
  Tooltip,
  Dropdown,
  MenuProps
} from 'antd';
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  BellOutlined,
  CheckOutlined,
  ReloadOutlined,
  SettingOutlined,
  CloseOutlined,
  MoreOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  AuditOutlined,
  CheckSquareOutlined,
  SendOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { theme } from 'antd';
import { useReportsNotifications, ReportsNotification } from '../context/ReportsNotificationContext';
import '../style/partials/notificationPanel.css';

const { Text, Paragraph } = Typography;

interface ReportsNotificationPanelProps {
  visible: boolean;
  onClose: () => void;
}

const ReportsNotificationPanel: React.FC<ReportsNotificationPanelProps> = ({ visible, onClose }) => {
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refetchNotifications
  } = useReportsNotifications();

  const navigate = useNavigate();
  const { theme: currentTheme } = useTheme();
  const isDarkMode = currentTheme.algorithm?.[0] === theme.darkAlgorithm;

  const themeTokens = useMemo(() => ({
    background: isDarkMode ? '#1f1f1f' : '#ffffff',
    textColor: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
    textColorSecondary: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
    borderColor: isDarkMode ? '#303030' : '#f0f0f0',
    unreadBackground: isDarkMode ? 'rgba(24, 144, 255, 0.1)' : 'rgba(24, 144, 255, 0.05)',
    itemHoverBackground: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)'
  }), [isDarkMode]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14', fontSize: 20 }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 20 }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 20 }} />;
    }
  };

  const getEntityIcon = (notification: ReportsNotification) => {
    const category = notification.category;
    const title = notification.title?.toLowerCase() || '';
    
    if (title.includes('review')) {
      return <AuditOutlined style={{ color: '#1890ff' }} />;
    } else if (title.includes('recommend')) {
      return <CheckSquareOutlined style={{ color: '#722ed1' }} />;
    } else if (title.includes('sanction') || title.includes('approved')) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    } else if (title.includes('rejected')) {
      return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
    } else if (title.includes('return')) {
      return <WarningOutlined style={{ color: '#faad14' }} />;
    } else if (title.includes('submit')) {
      return <SendOutlined style={{ color: '#13c2c2' }} />;
    } else if (title.includes('payment')) {
      return <DollarOutlined style={{ color: '#52c41a' }} />;
    } else if (category === 'approval') {
      return <CheckSquareOutlined style={{ color: '#722ed1' }} />;
    }
    return <FileTextOutlined style={{ color: '#1890ff' }} />;
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'system': return 'blue';
      case 'user': return 'cyan';
      case 'security': return 'red';
      case 'update': return 'green';
      case 'approval': return 'purple';
      case 'review': return 'orange';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'critical': return '#f5222d';
      case 'high': return '#fa8c16';
      case 'medium': return '#fadb14';
      case 'low': return '#52c41a';
      default: return '#1890ff';
    }
  };

  const formatTimestamp = (timestamp: Date): string => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  const handleNotificationClick = async (notification: ReportsNotification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
      return;
    }

    if (notification.entityId && notification.entityType === 'payment_report') {
      navigate(`/reports/payment-reports/${notification.entityId}`);
      onClose();
    }
  };

  const getNotificationMenuItems = (notification: ReportsNotification): MenuProps['items'] => [
    ...(notification.read ? [
      {
        key: 'unread',
        label: 'Mark as unread',
        icon: <EyeOutlined />,
        onClick: (e: any) => {
          e?.domEvent?.stopPropagation();
          markAsUnread(notification.id);
        },
      }
    ] : [
      {
        key: 'read',
        label: 'Mark as read',
        icon: <CheckOutlined />,
        onClick: (e: any) => {
          e?.domEvent?.stopPropagation();
          markAsRead(notification.id);
        },
      }
    ]),
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (e: any) => {
        e?.domEvent?.stopPropagation();
        deleteNotification(notification.id);
      },
    }
  ];

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
  }, [notifications]);

  const getActionText = (notification: ReportsNotification): string => {
    if (notification.actionText) {
      return notification.actionText;
    }
    
    const title = notification.title?.toLowerCase() || '';
    
    if (title.includes('review')) {
      return 'Review Report';
    } else if (title.includes('recommendation')) {
      return 'Recommend Report';
    } else if (title.includes('sanction')) {
      return 'Sanction Report';
    } else if (title.includes('approved') || title.includes('rejected') || title.includes('return')) {
      return 'View Report';
    }
    
    return 'View Details';
  };

  const headerActions = (
    <Space>
      <Tooltip title="Refresh notifications">
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={refetchNotifications}
          loading={isLoading}
          size="small"
        />
      </Tooltip>
      <Tooltip title="Mark all as read">
        <Button
          type="text"
          icon={<CheckOutlined />}
          onClick={markAllAsRead}
          disabled={unreadCount === 0}
          size="small"
        />
      </Tooltip>
      <Tooltip title="Notification settings">
        <Button
          type="text"
          icon={<SettingOutlined />}
          size="small"
        />
      </Tooltip>
    </Space>
  );

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space align="center">
            <BellOutlined />
            <span>Reports Notifications</span>
            {unreadCount > 0 && (
              <Badge
                count={unreadCount}
                size="small"
                style={{ backgroundColor: '#ff4d4f' }}
                className={unreadCount > 0 ? 'notification-badge-pulse' : ''}
              />
            )}
          </Space>
          {headerActions}
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={420}
      className="notification-panel reports-notification-panel"
      styles={{
        body: {
          padding: 0,
          background: themeTokens.background
        },
        header: {
          borderBottom: `1px solid ${themeTokens.borderColor}`,
          background: themeTokens.background
        }
      }}
      closeIcon={<CloseOutlined />}
    >
      {error && (
        <Alert
          message="Error loading notifications"
          description={error}
          type="error"
          showIcon
          style={{ margin: '16px' }}
          action={
            <Button size="small" onClick={refetchNotifications}>
              Retry
            </Button>
          }
        />
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Spin size="large" />
        </div>
      )}

      {!isLoading && !error && (
        <>
          {notifications.length > 0 && (
            <div style={{ padding: '16px', borderBottom: `1px solid ${themeTokens.borderColor}` }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text type="secondary">
                  {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                  {unreadCount > 0 && ` • ${unreadCount} unread`}
                </Text>
                {notifications.length > 0 && (
                  <Button
                    type="link"
                    size="small"
                    onClick={clearAllNotifications}
                    style={{ padding: 0, color: themeTokens.textColorSecondary }}
                  >
                    Clear all
                  </Button>
                )}
              </Space>
            </div>
          )}

          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: themeTokens.textColorSecondary }}>
                  No reports notifications
                </span>
              }
              style={{
                marginTop: '60px',
                color: themeTokens.textColorSecondary
              }}
            />
          ) : (
            <List
              itemLayout="vertical"
              dataSource={sortedNotifications}
              style={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}
              renderItem={(notification) => (
                <List.Item
                  key={notification.id}
                  className={`notification-priority-${notification.priority || 'medium'}`}
                  style={{
                    padding: '16px',
                    borderBottom: `1px solid ${themeTokens.borderColor}`,
                    backgroundColor: notification.read ? 'transparent' : themeTokens.unreadBackground,
                    cursor: notification.actionUrl || notification.entityId ? 'pointer' : 'default',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (notification.actionUrl || notification.entityId) {
                      e.currentTarget.style.backgroundColor = themeTokens.itemHoverBackground;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = notification.read ? 'transparent' : themeTokens.unreadBackground;
                  }}
                  onClick={() => handleNotificationClick(notification)}
                  actions={[
                    <Dropdown
                      menu={{ items: getNotificationMenuItems(notification) }}
                      trigger={['click']}
                      key="more"
                    >
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={notification.entityType ? getEntityIcon(notification) : getNotificationIcon(notification.type)}
                        style={{ backgroundColor: 'transparent' }}
                      />
                    }
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Text
                          strong={!notification.read}
                          style={{
                            color: themeTokens.textColor,
                            fontSize: '14px',
                            flex: 1
                          }}
                        >
                          {notification.title}
                        </Text>
                        {!notification.read && (
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#1890ff',
                              flexShrink: 0
                            }}
                          />
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <Paragraph
                          style={{
                            margin: 0,
                            marginBottom: '8px',
                            color: themeTokens.textColorSecondary,
                            fontSize: '13px',
                            lineHeight: '1.4'
                          }}
                          ellipsis={{ rows: 2, expandable: false }}
                        >
                          {notification.message}
                        </Paragraph>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <Space size="small">
                            {notification.category && (
                              <Tag
                                color={getCategoryColor(notification.category)}
                                style={{ fontSize: '11px', padding: '0 6px', margin: 0 }}
                              >
                                {notification.category}
                              </Tag>
                            )}
                            {notification.priority && (
                              <Tag
                                style={{
                                  fontSize: '11px',
                                  padding: '0 6px',
                                  margin: 0,
                                  backgroundColor: getPriorityColor(notification.priority),
                                  color: '#fff',
                                  border: 'none'
                                }}
                              >
                                {notification.priority}
                              </Tag>
                            )}
                          </Space>
                          <Text
                            type="secondary"
                            style={{ fontSize: '11px', color: themeTokens.textColorSecondary }}
                          >
                            {formatTimestamp(notification.timestamp)}
                          </Text>
                        </div>
                        {(notification.entityId || notification.actionUrl) && (
                          <Button
                            type="link"
                            size="small"
                            icon={getEntityIcon(notification)}
                            style={{
                              padding: '4px 0',
                              height: 'auto',
                              fontSize: '12px',
                              marginTop: '4px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            {getActionText(notification)} →
                          </Button>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default React.memo(ReportsNotificationPanel);
